import React, { useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import {
  getAllClasses,
  createClass,
  addOrUpdateSubject,
  addChaptersToSubject,
  removeChapterFromSubject,
  removeSubject,
  deleteClass,
} from "../../../services/operations/subjectAPI"; 

export default function SubjectDashboard() {
  const [classes, setClasses] = useState([]);
  const [expandedClasses, setExpandedClasses] = useState({}); // Tracks which accordions are open
  
  // Forms state
  const [isCreating, setIsCreating] = useState(false);
  const [newClassForm, setNewClassForm] = useState({ _id: "", class_name: "" });
  
  // State for dynamic inputs: keyed by classId or classId_subjectName
  const [subjectInputs, setSubjectInputs] = useState({});
  const [chapterInputs, setChapterInputs] = useState({}); 

  // Fetch all classes on mount
  useEffect(() => {
    fetchClasses();
  }, []);

  const fetchClasses = async () => {
    const response = await getAllClasses();
    if (response?.data) setClasses(response.data);
  };

  // Helper to update a specific class in the state array without refetching everything
  const updateClassInState = (updatedClass) => {
    setClasses((prev) => prev.map((c) => (c._id === updatedClass._id ? updatedClass : c)));
  };

  // Toggle Accordion
  const toggleClassAccordion = (classId) => {
    setExpandedClasses((prev) => ({
      ...prev,
      [classId]: !prev[classId],
    }));
  };

  // =========================================================
  // HANDLERS
  // =========================================================

  const handleCreateClass = async (e) => {
    e.preventDefault();
    if (!newClassForm._id || !newClassForm.class_name) return toast.error("Fill all fields");

    const response = await createClass({
      _id: newClassForm._id,
      class_name: newClassForm.class_name,
      subjects: {}
    });

    if (response?.data) {
      setClasses([...classes, response.data]); // Add to UI instantly
      setIsCreating(false);
      setNewClassForm({ _id: "", class_name: "" });
      // Auto-expand the new class
      setExpandedClasses((prev) => ({ ...prev, [response.data._id]: true }));
    }
  };

  const handleDeleteClass = async (classId) => {
    if (!window.confirm("Are you sure you want to delete this entire class?")) return;
    const response = await deleteClass(classId);
    if (response) {
      setClasses((prev) => prev.filter((c) => c._id !== classId));
    }
  };

  const handleAddSubject = async (e, classId) => {
    e.preventDefault();
    const newSubjectName = subjectInputs[classId];
    if (!newSubjectName?.trim()) return toast.error("Enter a subject name");

    const response = await addOrUpdateSubject(classId, newSubjectName.toLowerCase(), []);
    if (response?.data) {
      updateClassInState(response.data);
      setSubjectInputs({ ...subjectInputs, [classId]: "" }); // Clear input
    }
  };

  const handleDeleteSubject = async (classId, subjectName) => {
    if (!window.confirm(`Delete ${subjectName}?`)) return;
    const response = await removeSubject(classId, subjectName);
    if (response?.data) updateClassInState(response.data);
  };

  const handleAddChapter = async (classId, subjectName) => {
    const inputKey = `${classId}_${subjectName}`;
    const chapterName = chapterInputs[inputKey];
    if (!chapterName?.trim()) return toast.error("Enter a chapter name");

    const response = await addChaptersToSubject(classId, subjectName, [chapterName]);
    if (response?.data) {
      updateClassInState(response.data);
      setChapterInputs({ ...chapterInputs, [inputKey]: "" }); // Clear input
    }
  };

  const handleDeleteChapter = async (classId, subjectName, chapterToRemove) => {
    if (!window.confirm(`Remove "${chapterToRemove}"?`)) return;
    const response = await removeChapterFromSubject(classId, subjectName, chapterToRemove);
    if (response?.data) updateClassInState(response.data);
  };

  // =========================================================
  // RENDER UI
  // =========================================================

  return (
    <div className="max-w-6xl mx-auto p-6 font-sans">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-800">Curriculum Dashboard</h1>
        <button
          onClick={() => setIsCreating(!isCreating)}
          className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 font-medium shadow-sm transition-all"
        >
          {isCreating ? "Cancel" : "+ Create New Class"}
        </button>
      </div>

      {/* CREATE CLASS FORM */}
      {isCreating && (
        <form onSubmit={handleCreateClass} className="bg-white p-6 rounded-lg shadow-md border mb-8 max-w-md animate-fade-in">
          <h2 className="text-xl font-bold mb-4">Create New Class</h2>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Class ID (Must be unique)</label>
            <input
              type="text"
              placeholder="e.g. cbse-06"
              value={newClassForm._id}
              onChange={(e) => setNewClassForm({ ...newClassForm, _id: e.target.value })}
              className="border p-2 rounded w-full focus:ring focus:ring-blue-200 outline-none"
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Class Level / Name</label>
            <input
              type="text"
              placeholder="e.g. 6"
              value={newClassForm.class_name}
              onChange={(e) => setNewClassForm({ ...newClassForm, class_name: e.target.value })}
              className="border p-2 rounded w-full focus:ring focus:ring-blue-200 outline-none"
            />
          </div>
          <button type="submit" className="bg-green-600 text-white px-4 py-2 rounded w-full hover:bg-green-700 font-semibold shadow-sm">
            Save Class
          </button>
        </form>
      )}

      {/* CLASSES LIST */}
      <div className="space-y-4">
        {classes.length === 0 && !isCreating && (
          <p className="text-gray-500 text-center py-10 bg-gray-50 rounded-lg border border-dashed">No classes found. Create one to get started.</p>
        )}

        {classes.map((cls) => {
          const isExpanded = expandedClasses[cls._id];

          return (
            <div key={cls._id} className="bg-white rounded-lg shadow-sm border overflow-hidden">
              {/* Accordion Header */}
              <div className="flex justify-between items-center bg-gray-50 p-4 border-b">
                <div 
                  className="flex-1 cursor-pointer flex items-center gap-3" 
                  onClick={() => toggleClassAccordion(cls._id)}
                >
                  <span className="text-xl font-bold text-gray-800">Class: {cls.class_name}</span>
                  <span className="text-sm text-gray-500 bg-gray-200 px-2 py-1 rounded">ID: {cls._id}</span>
                  <span className="text-gray-400 text-sm ml-2">
                    ({Object.keys(cls.subjects || {}).length} subjects) {isExpanded ? "▲" : "▼"}
                  </span>
                </div>
                <button
                  onClick={() => handleDeleteClass(cls._id)}
                  className="bg-red-50 text-red-600 hover:bg-red-100 px-3 py-1.5 rounded text-sm font-semibold transition-colors border border-red-100"
                >
                  Delete Class
                </button>
              </div>

              {/* Accordion Body (Subjects & Chapters) */}
              {isExpanded && (
                <div className="p-6 bg-gray-50/50">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {cls.subjects && Object.entries(cls.subjects).map(([subjectName, subjectData]) => (
                      <div key={subjectName} className="bg-white border rounded-lg shadow-sm p-5">
                        
                        {/* Subject Header */}
                        <div className="flex justify-between items-center mb-4 border-b pb-2">
                          <h3 className="text-lg font-bold capitalize text-gray-800">{subjectName}</h3>
                          <button
                            onClick={() => handleDeleteSubject(cls._id, subjectName)}
                            className="text-red-500 hover:text-red-700 text-sm font-medium"
                          >
                            Delete Subject
                          </button>
                        </div>

                        {/* Chapters List */}
                        <ul className="space-y-2 mb-4 max-h-64 overflow-y-auto pr-2">
                          {subjectData.chapters?.length === 0 && (
                            <li className="text-gray-400 italic text-sm">No chapters added yet.</li>
                          )}
                          {subjectData.chapters?.map((chapter, idx) => (
                            <li key={idx} className="flex justify-between items-center bg-gray-50 p-2 rounded border border-gray-100 group">
                              <span className="text-gray-700 text-sm">{chapter}</span>
                              <button
                                onClick={() => handleDeleteChapter(cls._id, subjectName, chapter)}
                                className="text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Remove Chapter"
                              >
                                ✕
                              </button>
                            </li>
                          ))}
                        </ul>

                        {/* Add Chapter Input */}
                        <div className="flex gap-2">
                          <input
                            type="text"
                            placeholder="New chapter name..."
                            value={chapterInputs[`${cls._id}_${subjectName}`] || ""}
                            onChange={(e) => setChapterInputs({ ...chapterInputs, [`${cls._id}_${subjectName}`]: e.target.value })}
                            className="border p-2 rounded w-full text-sm outline-none focus:border-blue-400"
                            onKeyDown={(e) => e.key === 'Enter' && handleAddChapter(cls._id, subjectName)}
                          />
                          <button
                            onClick={() => handleAddChapter(cls._id, subjectName)}
                            className="bg-blue-50 text-blue-700 border border-blue-200 px-3 py-2 rounded hover:bg-blue-100 font-medium text-sm whitespace-nowrap"
                          >
                            Add
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Add New Subject Form (Specific to this class) */}
                  <form onSubmit={(e) => handleAddSubject(e, cls._id)} className="bg-white border border-gray-300 rounded-lg shadow-sm p-4 flex gap-4 max-w-md mt-6">
                    <input
                      type="text"
                      placeholder="Add new subject (e.g. History)"
                      value={subjectInputs[cls._id] || ""}
                      onChange={(e) => setSubjectInputs({ ...subjectInputs, [cls._id]: e.target.value })}
                      className="border p-2 rounded w-full outline-none focus:border-gray-800"
                    />
                    <button type="submit" className="bg-gray-800 text-white px-4 py-2 rounded hover:bg-gray-900 font-medium whitespace-nowrap shadow-sm">
                      + Add Subject
                    </button>
                  </form>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}